import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { billingApi } from '../../api';
import { PageHeader, LoadingState, ErrorState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

const FEATURE_KEYS = [
  { key: 'accounting', label: 'Accounting + GL' },
  { key: 'hrm', label: 'HR & payroll' },
  { key: 'purchasing', label: 'Purchasing + GRN' },
  { key: 'ecommerce', label: 'E-commerce' },
  { key: 'multi_branch', label: 'Multi-branch' },
  { key: 'advanced_reporting', label: 'Advanced reporting' },
] as const;

const LIMIT_KEYS = [
  { key: 'max_products', label: 'Max products' },
  { key: 'max_branches', label: 'Max branches' },
  { key: 'max_users', label: 'Max users' },
] as const;

export function PlanManagementPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => billingApi.adminListPlans() });

  if (q.isLoading) return <div className="p-6"><LoadingState /></div>;
  if (q.error) return <div className="p-6"><ErrorState error={q.error} /></div>;

  const plans = (q.data ?? []) as any[];

  return (
    <div className="p-6">
      <PageHeader
        eyebrow="Administration"
        title="Plan management"
        subtitle="Edit pricing, feature labels, and the enforced entitlement matrix for each plan."
      />
      <div className="space-y-5 mt-2">
        {plans.map((p) => (
          <PlanEditor key={p.id} plan={p}
            onSaved={() => { qc.invalidateQueries({ queryKey: ['admin', 'plans'] }); qc.invalidateQueries({ queryKey: ['billing', 'overview'] }); }} />
        ))}
      </div>
      <p className="text-xs text-ink-500 mt-6 max-w-2xl">
        Changes apply within a minute (cached). Feature gates control which modules a tenant on this plan
        can access; numeric limits are enforced when creating products, branches, and users. A blank limit
        means unlimited.
      </p>
    </div>
  );
}

function PlanEditor({ plan, onSaved }: { plan: any; onSaved: () => void }) {
  const ent = plan.entitlements ?? {};
  const [name, setName] = useState(plan.name);
  const [monthly, setMonthly] = useState(String(plan.monthly ?? 0));
  const [yearly, setYearly] = useState(String(plan.yearly ?? 0));
  const [features, setFeatures] = useState<string[]>(plan.features ?? []);
  const [gates, setGates] = useState<Record<string, boolean>>(
    Object.fromEntries(FEATURE_KEYS.map((f) => [f.key, !!(ent.features ?? {})[f.key]]))
  );
  const [limits, setLimits] = useState<Record<string, string>>(
    Object.fromEntries(LIMIT_KEYS.map((l) => {
      const v = (ent.limits ?? {})[l.key];
      return [l.key, v == null ? '' : String(v)];
    }))
  );
  const [isActive, setIsActive] = useState(plan.is_active ?? true);

  const mut = useMutation({
    mutationFn: () => billingApi.adminUpdatePlan(plan.id, {
      name,
      monthly: Number(monthly) || 0,
      yearly: Number(yearly) || 0,
      features: features.filter((f) => f.trim()),
      entitlements: {
        features: gates,
        limits: Object.fromEntries(LIMIT_KEYS.map((l) => {
          const raw = limits[l.key].trim();
          return [l.key, raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0)];
        })),
      },
      isActive,
    }),
    onSuccess: () => { toast.success(`${name} saved`); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <input className="input !w-44 font-semibold" value={name} onChange={(e) => setName(e.target.value)} />
          <span className="text-xs font-mono text-ink-400 bg-canvas-subtle px-2 py-0.5 rounded">{plan.id}</span>
          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
          </label>
        </div>
        <button className="btn-primary !py-2 !px-4 text-sm" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save plan'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Pricing */}
        <div>
          <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">Pricing (PKR)</h4>
          <label className="label">Monthly</label>
          <input className="input mb-2" type="number" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)} />
          <label className="label">Yearly</label>
          <input className="input" type="number" min="0" value={yearly} onChange={(e) => setYearly(e.target.value)} />
        </div>

        {/* Feature gates (enforced) */}
        <div>
          <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">Feature access (enforced)</h4>
          <div className="space-y-1.5">
            {FEATURE_KEYS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm text-ink-700">
                <input type="checkbox" checked={gates[f.key]}
                  onChange={(e) => setGates((g) => ({ ...g, [f.key]: e.target.checked }))} />
                {f.label}
              </label>
            ))}
          </div>
        </div>

        {/* Numeric limits (enforced) */}
        <div>
          <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">Limits (blank = unlimited)</h4>
          {LIMIT_KEYS.map((l) => (
            <div key={l.key} className="mb-2">
              <label className="label">{l.label}</label>
              <input className="input" type="number" min="0" placeholder="Unlimited"
                value={limits[l.key]} onChange={(e) => setLimits((s) => ({ ...s, [l.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>

      {/* Display feature labels (the bullet list shown on the pricing cards) */}
      <div className="mt-4">
        <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">Card bullet points</h4>
        <div className="space-y-1.5">
          {features.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input !py-1.5 text-sm" value={f}
                onChange={(e) => setFeatures((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} />
              <button className="text-ink-400 hover:text-danger px-2" title="Remove"
                onClick={() => setFeatures((arr) => arr.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button className="text-sm text-finance-700 font-medium hover:underline"
            onClick={() => setFeatures((arr) => [...arr, ''])}>+ Add bullet</button>
        </div>
      </div>
    </div>
  );
}
